import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, Copy, LogOut, Users, MousePointerClick, FileText, Share2,
    MessageCircle, BadgeCheck, Send, ChevronDown, ChevronUp, Trophy, Medal,
    Gift, Wallet, Plane, Gem, Award,
} from "lucide-react";
import { assocDashboard, assocUpdateProfile, assocSubmitPackage, formatApiError, fetchAssocShareCard, fetchAssocShareCaption, assocLeaderboard } from "@/lib/api";
import { SharePackageModal } from "@/components/SharePackageModal";
import { PartnerTerms } from "@/components/landing/PartnerTerms";

const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-saffron";
const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";
const ROLE_LABEL = {
    explorer: "Ghumne Chaley Explorer", insider: "Ghumne Chaley Travel Insider",
    ambassador: "Ghumne Chaley Travel Ambassador", privilege_member: "Ghumne Chaley Privilege Member",
};
const SUB_STATUS = { pending: "bg-gold/20 text-yellow-800", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-600" };

// Informational-only benefits per associate category. Order matters; no tracking/unlocking.
const BENEFITS = {
    explorer: [
        { icon: "gift", title: "Return Gift", highlight: null,
          text: "A special return gift for every traveler referred by you." },
    ],
    insider: [
        { icon: "gift", title: "Return Gift", highlight: null,
          text: "A special return gift for every traveler referred by you." },
        { icon: "wallet", title: "5% Commission", highlight: "5%",
          text: "Earn 5% commission on every completed booking generated through your referral." },
    ],
    ambassador: [
        { icon: "gift", title: "Return Gift", highlight: null,
          text: "A special return gift for every traveler referred by you." },
        { icon: "wallet", title: "4% Commission", highlight: "4%",
          text: "Earn 4% commission on every completed booking generated through your referral." },
        { icon: "plane", title: "Exclusive Family Trip", highlight: "1 trip · up to ₹1,00,000",
          text: "Enjoy 1 complimentary family trip worth up to ₹1,00,000 on achieving an annual revenue target of ₹25 lakh." },
    ],
    privilege_member: [
        { icon: "gift", title: "Return Gift", highlight: null,
          text: "A special return gift for every traveler referred by you." },
        { icon: "wallet", title: "3% Commission", highlight: "3%",
          text: "Earn 3% commission on every completed booking generated through your referral." },
        { icon: "gem", title: "Exclusive Family Trips", highlight: "2 trips · up to ₹1,00,000 each",
          text: "Enjoy 2 complimentary family trips, each worth up to ₹1,00,000, on achieving an annual revenue target of ₹75 lakh." },
    ],
};

const BENEFIT_ICON = { gift: Gift, wallet: Wallet, plane: Plane, gem: Gem };

function BenefitsSection({ role }) {
    const items = BENEFITS[role] || [];
    if (!items.length) return null;
    return (
        <div className="rounded-3xl border border-stone-200 bg-gradient-to-br from-cream to-white p-6 shadow-sm" data-testid="benefits-section">
            <div className="flex items-center gap-2.5">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-saffron/15 text-saffron"><Award size={18} /></span>
                <div>
                    <h2 className="font-display font-black text-lg text-navy" data-testid="benefits-heading">Your Exclusive Associate Benefits</h2>
                    <p className="text-xs text-stone-500">Discover the rewards, earning opportunities and exclusive privileges available with your {ROLE_LABEL[role] || "associate"} membership.</p>
                </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="benefits-cards">
                {items.map((b, i) => {
                    const Icon = BENEFIT_ICON[b.icon] || Gift;
                    return (
                        <div key={i} className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={`benefit-card-${i}`}>
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gold/20 text-navy"><Icon size={20} /></span>
                            <p className="mt-3 font-display font-black text-navy text-sm">{b.title}</p>
                            {b.highlight && <p className="mt-0.5 font-display font-black text-saffron text-base" data-testid={`benefit-highlight-${i}`}>{b.highlight}</p>}
                            <p className="mt-1.5 text-xs text-stone-500 leading-relaxed">{b.text}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SubmissionForm({ onDone }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState({ title: "", destination: "", days: 2, nights: 1, price_adult: "", category: "Group Tours", summary: "", inclusions: "", itinerary: "" });
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await assocSubmitPackage({ ...f, days: Number(f.days), nights: Number(f.nights), price_adult: Number(f.price_adult) });
            toast.success("Package submitted for review — it goes live only after approval");
            setOpen(false);
            setF({ title: "", destination: "", days: 2, nights: 1, price_adult: "", category: "Group Tours", summary: "", inclusions: "", itinerary: "" });
            onDone();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="submission-block">
            <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-5 py-4 text-left hover:bg-cream/50" data-testid="submission-toggle">
                <FileText className="h-4 w-4 text-saffron" />
                <span className="font-display font-black text-sm text-navy uppercase tracking-wider">Submit a package idea</span>
                {open ? <ChevronUp className="ml-auto h-4 w-4 text-stone-400" /> : <ChevronDown className="ml-auto h-4 w-4 text-stone-400" />}
            </button>
            {open && (
                <form onSubmit={submit} className="border-t border-stone-200 p-5 grid sm:grid-cols-2 gap-4" data-testid="submission-form">
                    <div><label className={lab}>Package title *</label><input className={inp} value={f.title} onChange={set("title")} data-testid="sub-title" placeholder="Lonavala Adventure Camp" /></div>
                    <div><label className={lab}>Destination *</label><input className={inp} value={f.destination} onChange={set("destination")} data-testid="sub-destination" placeholder="Lonavala" /></div>
                    <div className="grid grid-cols-3 gap-3">
                        <div><label className={lab}>Days</label><input className={inp} type="number" min="1" value={f.days} onChange={set("days")} data-testid="sub-days" /></div>
                        <div><label className={lab}>Nights</label><input className={inp} type="number" min="0" value={f.nights} onChange={set("nights")} data-testid="sub-nights" /></div>
                        <div><label className={lab}>Price/adult ₹ *</label><input className={inp} type="number" min="1" value={f.price_adult} onChange={set("price_adult")} data-testid="sub-price" placeholder="4500" /></div>
                    </div>
                    <div><label className={lab}>Category</label>
                        <select className={inp} value={f.category} onChange={set("category")} data-testid="sub-category">
                            {["Group Tours", "School Trips", "College Trips", "Corporate", "Beach", "Mountains", "Adventure", "Pilgrimage"].map((c) => <option key={c}>{c}</option>)}
                        </select></div>
                    <div className="sm:col-span-2"><label className={lab}>Summary</label><textarea rows={2} className={inp} value={f.summary} onChange={set("summary")} data-testid="sub-summary" placeholder="One-paragraph pitch" /></div>
                    <div className="sm:col-span-2"><label className={lab}>Inclusions (one per line)</label><textarea rows={2} className={inp} value={f.inclusions} onChange={set("inclusions")} data-testid="sub-inclusions" placeholder={"Stay\nMeals\nTransport"} /></div>
                    <div className="sm:col-span-2"><label className={lab}>Itinerary (day-wise)</label><textarea rows={3} className={inp} value={f.itinerary} onChange={set("itinerary")} data-testid="sub-itinerary" placeholder={"Day 1: …\nDay 2: …"} /></div>
                    <button type="submit" disabled={busy} data-testid="sub-submit"
                        className="sm:col-span-2 rounded-full bg-navy py-3 text-xs font-display font-black uppercase tracking-wider text-white hover:bg-navy-light disabled:opacity-60 flex items-center justify-center gap-2">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit for approval
                    </button>
                </form>
            )}
        </div>
    );
}

export default function PartnerDashboardPage() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [sharing, setSharing] = useState(null); // package for the share-card modal
    const [lb, setLb] = useState(null); // monthly referral leaderboard
    const [err, setErr] = useState("");
    const [editOpen, setEditOpen] = useState(false);
    const [profile, setProfile] = useState({ name: "", mobile: "" });

    const load = useCallback(async () => {
        try {
            const d = await assocDashboard();
            setData(d);
            setProfile({ name: d.associate.name, mobile: d.associate.mobile });
            assocLeaderboard().then(setLb).catch(() => {});
        } catch (e) {
            const code = e.response?.status;
            if (code === 401 || code === 403) {
                localStorage.removeItem("gc_partner_token");
                setErr(formatApiError(e.response?.data?.detail) || "Please log in again");
                setTimeout(() => navigate("/partner/login"), 1500);
            } else {
                setErr("Could not load your dashboard — please refresh");
            }
        }
    }, [navigate]);

    useEffect(() => {
        if (!localStorage.getItem("gc_partner_token")) { navigate("/partner/login"); return; }
        load();
    }, [load, navigate]);

    const copy = (text, what) => { navigator.clipboard.writeText(text); toast.success(`${what} copied`); };

    const saveProfile = async () => {
        try {
            await assocUpdateProfile(profile);
            toast.success("Profile updated");
            setEditOpen(false);
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    if (err) return <div className="min-h-screen bg-cream flex items-center justify-center text-sm text-stone-600" data-testid="partner-error">{err}</div>;
    if (!data) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-saffron" /></div>;

    const a = data.associate;
    const roleLabel = ROLE_LABEL[a.role] || a.role;

    return (
        <div className="min-h-screen bg-cream pb-16" data-testid="partner-dashboard">
            {/* header */}
            <div className="bg-navy text-white">
                <div className="max-w-6xl mx-auto px-6 py-8 flex flex-wrap items-center gap-4">
                    <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-wider text-saffron">{roleLabel}</p>
                        <h1 className="font-display font-black text-2xl md:text-3xl truncate" data-testid="partner-name">Namaste, {a.name.split(" ")[0]}</h1>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <button onClick={() => { setProfile({ name: a.name, mobile: a.mobile }); setEditOpen(true); }} data-testid="edit-profile-btn"
                            className="rounded-full border border-white/30 px-4 py-2 text-[11px] font-black uppercase tracking-wider hover:bg-white/10">Edit profile</button>
                        <button onClick={() => { localStorage.removeItem("gc_partner_token"); localStorage.removeItem("gc_partner"); navigate("/partners"); }} data-testid="partner-logout"
                            className="inline-flex items-center gap-1.5 rounded-full bg-saffron px-4 py-2 text-[11px] font-black uppercase tracking-wider hover:bg-saffron-dark"><LogOut className="h-3.5 w-3.5" /> Logout</button>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 mt-6 space-y-6">
                {/* ID + group link + stats */}
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="rounded-2xl border-2 border-saffron/40 bg-white p-5 flex items-center gap-4" data-testid="associate-id-card">
                        <div className="min-w-0">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Your Associate ID / referral code</p>
                            <div className="mt-2 flex items-center gap-2">
                                <span className="font-mono font-black text-xl text-navy tracking-wider" data-testid="associate-id">{a.associate_id}</span>
                                <button onClick={() => copy(a.associate_id, "Associate ID")} data-testid="copy-id-btn" className="text-stone-400 hover:text-saffron"><Copy className="h-4 w-4" /></button>
                            </div>
                            <p className="mt-1 text-[11px] text-stone-400">Travellers enter this code while enquiring or booking — every referral gets credited to you.</p>
                        </div>
                        <img src={`/badges/${a.role}.png`} alt={`${roleLabel} badge`} loading="lazy" className="ml-auto h-24 w-auto object-contain shrink-0" data-testid="associate-role-badge" />
                    </div>
                    {a.role === "explorer" ? (
                        <div className="rounded-2xl border border-stone-200 bg-gradient-to-br from-cream to-white p-5" data-testid="explorer-benefits-card">
                            <div className="flex items-center gap-2">
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-saffron/15 text-saffron"><Award size={15} /></span>
                                <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Your exclusive associate benefits</p>
                            </div>
                            <div className="mt-3 rounded-xl border border-stone-200 bg-white p-4" data-testid="explorer-benefit-return-gift">
                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gold/20 text-navy"><Gift size={18} /></span>
                                <p className="mt-2 font-display font-black text-navy text-sm">Return Gift</p>
                                <p className="mt-1 text-xs text-stone-500 leading-relaxed">A special return gift for every traveler referred by you.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="group-link-card">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Your community WhatsApp group</p>
                            {data.group_link ? (
                                <a href={data.group_link} target="_blank" rel="noreferrer" data-testid="group-link-btn"
                                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-xs font-black uppercase tracking-wider text-white hover:opacity-90">
                                    <MessageCircle className="h-4 w-4" /> Join the group
                                </a>
                            ) : (
                                <p className="mt-3 text-xs text-amber-700 bg-amber-50 rounded-lg p-3" data-testid="group-link-missing">
                                    Your category's group link is being set up by our team — it will appear here and reach you by email.
                                </p>
                            )}
                        </div>
                    )}
                    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="stats-card">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Your impact</p>
                        <div className="mt-2 flex gap-6">
                            <div><p className="font-display font-black text-2xl text-navy" data-testid="stat-clicks">{data.stats.clicks}</p>
                                <p className="text-[11px] text-stone-500 flex items-center gap-1"><MousePointerClick className="h-3 w-3" /> Link visits</p></div>
                            <div><p className="font-display font-black text-2xl text-navy" data-testid="stat-enquiries">{data.stats.enquiries}</p>
                                <p className="text-[11px] text-stone-500 flex items-center gap-1"><Users className="h-3 w-3" /> Enquiries</p></div>
                            <div><p className="font-display font-black text-2xl text-saffron" data-testid="stat-bookings">{data.stats.bookings || 0}</p>
                                <p className="text-[11px] text-stone-500 flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Paid bookings</p></div>
                        </div>
                        {(data.bookings || []).length > 0 && (
                            <div className="mt-3 border-t border-stone-100 pt-3" data-testid="recent-bookings">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 mb-1.5">Recent bookings via your code</p>
                                {data.bookings.slice(0, 5).map((b, i) => (
                                    <p key={i} className="text-xs text-stone-600">
                                        <span className="font-bold text-navy">{b.package}</span> · {b.reference} · advance {b.advance ? `₹${b.advance.toLocaleString("en-IN")}` : "—"}
                                        <span className="text-stone-400"> · {new Date(b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* exclusive associate benefits — informational, above the leaderboard (explorers see it in the top card instead) */}
                {a.role !== "explorer" && <BenefitsSection role={a.role} />}

                {/* monthly referral leaderboard */}
                {lb && (
                    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="leaderboard-card">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <h2 className="font-display font-black text-lg text-navy flex items-center gap-2">
                                <Trophy className="h-5 w-5 text-saffron" />
                                Referral Leaderboard
                                <span className="text-stone-400 text-sm font-body font-normal">
                                    · {new Date(lb.month + "-02").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
                                </span>
                            </h2>
                            <p className="text-[11px] text-stone-400">Enquiry = 10 pts · Link visit = 1 pt</p>
                        </div>
                        {lb.entries.length === 0 ? (
                            <p className="mt-3 text-sm text-stone-500" data-testid="leaderboard-empty">
                                No referrals yet this month — share your first card or link and take the top spot!
                            </p>
                        ) : (
                            <ol className="mt-4 space-y-2">
                                {lb.entries.map((e) => (
                                    <li key={e.rank}
                                        className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 ${e.you ? "border-saffron bg-saffron/5" : "border-stone-100"}`}
                                        data-testid={e.you ? "leaderboard-you" : `leaderboard-row-${e.rank}`}>
                                        <span className="w-8 text-center shrink-0">
                                            {e.rank <= 3
                                                ? <Medal className={`h-5 w-5 mx-auto ${["text-yellow-500", "text-stone-400", "text-amber-700"][e.rank - 1]}`} />
                                                : <span className="font-black text-stone-400 text-sm">{e.rank}</span>}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-navy text-sm truncate">
                                                {e.name} {e.you && <span className="text-saffron text-[10px] font-black uppercase">(You)</span>}
                                            </p>
                                            <p className="text-[10px] text-stone-400 uppercase tracking-wider">{ROLE_LABEL[e.role] || e.role}</p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="font-display font-black text-navy">{e.points} <span className="text-[10px] font-body font-normal text-stone-400">pts</span></p>
                                            <p className="text-[10px] text-stone-400">{e.enquiries} enquiries · {e.clicks} visits</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        )}
                        {lb.you && lb.you.rank > 20 && (
                            <p className="mt-3 text-xs text-stone-500">Your rank this month: <span className="font-bold text-navy">#{lb.you.rank}</span> ({lb.you.points} pts)</p>
                        )}
                        {!lb.you && lb.entries.length > 0 && (
                            <p className="mt-3 text-xs text-stone-500">You have no referral points yet this month — share a package card to get on the board!</p>
                        )}
                    </div>
                )}

                {/* packages */}
                <div>
                    <h2 className="font-display font-black text-lg text-navy" data-testid="packages-heading">
                        Packages you can promote <span className="text-stone-400 text-sm font-body font-normal">({data.packages.length})</span>
                    </h2>
                    {a.role === "privilege_member" && <p className="text-xs text-stone-500 mt-0.5">Showing your authorized premium & luxury inventory.</p>}
                    <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.packages.map((p) => (
                            <div key={p.slug} className="rounded-2xl border border-stone-200 bg-white overflow-hidden shadow-sm" data-testid={`promote-card-${p.slug}`}>
                                <div className="relative">
                                    {p.hero_image ? <img src={p.hero_image} alt={p.title} className="h-36 w-full object-cover" /> : <div className="h-36 bg-navy/5 flex items-center justify-center text-stone-300 text-xs">GhumneChaley.com</div>}
                                    {p.commission && (
                                        <span data-testid={`earn-badge-${p.slug}`} title={p.commission.estimated ? `Estimated ${p.commission.per_person ? "per person" : ""} commission at ${p.commission.rate}%` : p.commission.label}
                                            className="absolute top-2 right-2 rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-black text-white shadow-md">
                                            {p.commission.estimated ? `EARN ₹${p.commission.amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "Commission after pricing"}
                                        </span>
                                    )}
                                </div>
                                <div className="p-4">
                                    <p className="font-bold text-navy leading-tight">{p.title}</p>
                                    <p className="text-xs text-stone-500 mt-0.5">{p.destination} · {p.days}D/{p.nights}N{p.price_from ? ` · from ₹${p.price_from.toLocaleString("en-IN")}` : ""}</p>
                                    {p.commission && p.commission.estimated && p.commission.per_person && (
                                        <p className="text-[10px] text-stone-400" data-testid={`earn-note-${p.slug}`}>Estimated commission per person · final amount on confirmed booking</p>
                                    )}
                                    <div className="mt-3 flex gap-2">
                                        <button onClick={() => copy(p.share_url, "Share link")} data-testid={`copy-link-${p.slug}`}
                                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-navy px-3 py-2 text-[10px] font-black uppercase tracking-wider text-navy hover:bg-navy hover:text-white">
                                            <Copy className="h-3 w-3" /> Copy link
                                        </button>
                                        <a href={`https://wa.me/?text=${encodeURIComponent(`Check out ${p.title} with GhumneChaley.com! ${p.share_url} (Use my code ${a.associate_id} while booking)`)}`}
                                            target="_blank" rel="noreferrer" data-testid={`wa-share-${p.slug}`}
                                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#25D366] px-3 py-2 text-[10px] font-black uppercase tracking-wider text-white hover:opacity-90">
                                            <Share2 className="h-3 w-3" /> WhatsApp
                                        </a>
                                    </div>
                                    <button onClick={() => setSharing(p)} data-testid={`share-card-${p.slug}`}
                                        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-saffron px-3 py-2 text-[10px] font-black uppercase tracking-wider text-white hover:brightness-95">
                                        <Share2 className="h-3 w-3" /> Share card (Insta / FB) — with my code
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {data.packages.length === 0 && <p className="mt-4 text-sm text-stone-500">No packages assigned yet — check back soon.</p>}
                </div>

                {sharing && (
                    <SharePackageModal
                        pkg={sharing}
                        fetchCard={fetchAssocShareCard}
                        fetchCaption={fetchAssocShareCaption}
                        shareUrl={sharing.share_url}
                        refCode={data.associate.associate_id}
                        onClose={() => setSharing(null)}
                    />
                )}

                {/* package submissions (insider / ambassador / privilege member) */}
                {["insider", "ambassador", "privilege_member"].includes(a.role) && (
                    <div className="space-y-4">
                        <SubmissionForm onDone={load} />
                        {data.submissions.length > 0 && (
                            <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="submissions-list">
                                <h3 className="font-display font-black text-sm text-navy uppercase tracking-wider">My submissions</h3>
                                <div className="mt-3 space-y-2">
                                    {data.submissions.map((s) => (
                                        <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-stone-100 p-3" data-testid={`submission-${s.id}`}>
                                            <div className="min-w-0">
                                                <p className="font-bold text-navy text-sm">{s.title} <span className="font-normal text-stone-400">· {s.destination}</span></p>
                                                {s.review_note && <p className="text-xs text-stone-500 mt-0.5">Note: {s.review_note}</p>}
                                            </div>
                                            <span className={`ml-auto rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${SUB_STATUS[s.status]}`} data-testid={`submission-status-${s.id}`}>{s.status}</span>
                                            {s.status === "approved" && s.package_slug && (
                                                <Link to={`/package/${s.package_slug}`} className="text-[10px] font-black uppercase tracking-wider text-saffron hover:underline">View live →</Link>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* enquiries */}
                <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="enquiries-card">
                    <h3 className="font-display font-black text-sm text-navy uppercase tracking-wider">Enquiries credited to you</h3>
                    {data.enquiries.length === 0 ? (
                        <p className="mt-3 text-sm text-stone-500">None yet — share your links or code to get started.</p>
                    ) : (
                        <div className="mt-3 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                                    <th className="py-2 pr-3">Traveller</th><th className="py-2 pr-3">Trip</th><th className="py-2 pr-3">Status</th><th className="py-2">Date</th></tr></thead>
                                <tbody>
                                    {data.enquiries.map((l) => (
                                        <tr key={l.id} className="border-b border-stone-100" data-testid={`enquiry-${l.id}`}>
                                            <td className="py-2.5 pr-3 font-bold text-navy">{l.name}</td>
                                            <td className="py-2.5 pr-3 text-stone-600">{l.package_name || l.destination || "—"}</td>
                                            <td className="py-2.5 pr-3"><span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-stone-600">{l.status}</span></td>
                                            <td className="py-2.5 text-xs text-stone-400">{new Date(l.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* terms & conditions — below enquiries */}
                <PartnerTerms compact />
            </div>

            {/* edit profile modal */}
            {editOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4" data-testid="edit-profile-modal">
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                        <h3 className="font-display font-black text-lg text-navy">Edit profile</h3>
                        <div className="mt-4 space-y-3">
                            <div><label className={lab}>Name</label><input className={inp} value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} data-testid="edit-name" /></div>
                            <div><label className={lab}>Mobile</label><input className={inp} value={profile.mobile} onChange={(e) => setProfile((p) => ({ ...p, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))} data-testid="edit-mobile" /></div>
                            <p className="text-[11px] text-stone-400">Email and Associate ID can't be changed — contact the team if needed.</p>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button onClick={() => setEditOpen(false)} className="rounded-full border border-stone-300 px-4 py-2 text-xs font-black uppercase tracking-wider text-stone-500" data-testid="edit-cancel">Cancel</button>
                            <button onClick={saveProfile} className="rounded-full bg-navy px-4 py-2 text-xs font-black uppercase tracking-wider text-white" data-testid="edit-save">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
